/**
 * P4 — Report-branding + style-presets + per-brand portal-settings API client.
 *
 * Agency employee surface (Bearer = agency `emp_` key). Backend contract
 * (central-api Modules/ClientReporting — consume exactly, do NOT change it):
 *
 *   GET  report-style-presets
 *        → { data: ReportStylePreset[] }                    [portal.branding.manage]
 *   GET  report-branding                                    [portal.branding.manage]
 *        → { data: ReportBranding[], resolved?: ResolvedReportBranding }
 *          (agency default + per-brand overrides; `resolved` is the agency-level
 *          branding in effect, also when no row is saved)
 *   POST report-branding {preset_key, shop_id?, tokens?, design_md_override?}
 *        → 201 { data: ReportBranding }
 *   GET  report-branding/{id}          → { data: ReportBranding }
 *   PUT  report-branding/{id} {…patch} → { data: ReportBranding }
 *   DELETE report-branding/{id}
 *   POST report-branding/{id}/assets   (multipart: logo_light|logo_dark|favicon|font)
 *        → { data: ReportBranding }
 *   GET  report-branding/resolve?shop_slug=                  [employee_auth + brand access]
 *        → { data: ResolvedReportBranding }  (the compiled branding, incl. css_vars)
 *   GET  report-branding/resolve?scope=organisation          [employee_auth]
 *        → { data: ResolvedReportBranding }  (agency level, as the client portal uses)
 *
 *   GET  shops/{slug}/client-portal-settings                [portal.settings.manage]
 *        → { data: ClientPortalSettings }
 *   PUT  shops/{slug}/client-portal-settings {…}
 *        → { data: ClientPortalSettings }
 */
import { request, requestMultipart, toQuery } from './client-reports-http';

// ---------- wire types ----------

export type BrandingMode = 'dark' | 'light';

export interface BrandingColors {
  bg: string;
  panel: string;
  accent: string;
  accent_2: string;
  text: string;
  text_muted: string;
}

export interface BrandingFonts {
  heading: string;
  body: string;
}

export interface BrandingTokens {
  mode: BrandingMode;
  colors: BrandingColors;
  fonts: BrandingFonts;
}

export interface ReportStylePreset {
  key: string;
  name: string;
  description: string | null;
  preview_thumbnail_path: string | null;
  /** The preset's baseline tokens — used to seed a fresh override. */
  default_tokens: BrandingTokens | null;
  is_active: boolean;
}

export interface ReportBranding {
  id: string;
  organisation_id: string | number;
  /** null = agency default; non-null = per-brand override. */
  shop_id: number | string | null;
  /** Some backends echo the slug alongside the id for convenience. */
  shop_slug?: string | null;
  preset_key: string | null;
  design_md_override: string | null;
  tokens: BrandingTokens | null;
  logo_light_path: string | null;
  logo_dark_path: string | null;
  favicon_path: string | null;
  font_assets: Record<string, unknown> | null;
  is_default: boolean;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UpsertBrandingBody {
  preset_key?: string | null;
  /** Omit / null = agency default row; a slug/id creates a per-brand override. */
  shop_id?: number | string | null;
  shop_slug?: string | null;
  tokens?: BrandingTokens | null;
  design_md_override?: string | null;
  is_active?: boolean;
}

export interface UpdateBrandingPatch {
  preset_key?: string | null;
  tokens?: BrandingTokens | null;
  design_md_override?: string | null;
  is_active?: boolean;
}

/**
 * The compiled branding for one brand (`ReportBrandingResolver::resolve`):
 * brand override, else the agency default row, else defaults from the
 * agency's existing design settings. Only the fields the client reads are
 * typed; the payload also carries design_md, assets and the agency name.
 */
export interface ResolvedReportBranding {
  shop_slug?: string | null;
  source?: 'shop_override' | 'org_default' | 'defaults' | string;
  preset?: { key: string; name: string } | null;
  tokens?: Partial<BrandingTokens> | null;
  /**
   * `--r-*` custom property → value, compiled from the tokens with the same
   * mapping the portal-bootstrap `branding.css_vars` uses. PHP encodes an
   * empty map as `[]`, so treat anything that is not a plain object as none.
   */
  css_vars?: Record<string, string> | unknown[] | null;
}

// ---------- per-brand client-portal settings ----------

export interface ClientPortalSettings {
  changelog_enabled: boolean;
  changelog_categories: string[] | null;
  changelog_lookback_days: number | null;
  deep_dive_enabled: boolean;
  template_report_id: string | null;
}

// ---------- presets ----------

export async function listReportStylePresets(): Promise<ReportStylePreset[]> {
  const res = await request<{ data?: ReportStylePreset[] }>(
    'report-style-presets',
    { method: 'GET' }
  );
  return res.data ?? [];
}

// ---------- branding CRUD ----------

export async function listReportBranding(): Promise<ReportBranding[]> {
  const res = await request<{ data?: ReportBranding[] }>('report-branding', {
    method: 'GET',
  });
  return res.data ?? [];
}

/**
 * The branding rows plus the agency-level branding actually in effect
 * (`resolved`). Older backends omit `resolved`; it is then null.
 */
export async function listReportBrandingWithResolved(): Promise<{
  rows: ReportBranding[];
  resolved: ResolvedReportBranding | null;
}> {
  const res = await request<{
    data?: ReportBranding[];
    resolved?: ResolvedReportBranding | null;
  }>('report-branding', { method: 'GET' });
  const resolved =
    res.resolved &&
    typeof res.resolved === 'object' &&
    !Array.isArray(res.resolved)
      ? res.resolved
      : null;
  return { rows: res.data ?? [], resolved };
}

export async function getReportBranding(id: string): Promise<ReportBranding> {
  const res = await request<{ data: ReportBranding }>(
    `report-branding/${encodeURIComponent(id)}`,
    { method: 'GET' }
  );
  return res.data;
}

export async function createReportBranding(
  body: UpsertBrandingBody
): Promise<ReportBranding> {
  const res = await request<{ data: ReportBranding }>('report-branding', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

export async function updateReportBranding(
  id: string,
  patch: UpdateBrandingPatch
): Promise<ReportBranding> {
  const res = await request<{ data: ReportBranding }>(
    `report-branding/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(patch) }
  );
  return res.data;
}

export async function deleteReportBranding(id: string): Promise<void> {
  await request<unknown>(`report-branding/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export type BrandingAssetKind = 'logo_light' | 'logo_dark' | 'favicon' | 'font';

/**
 * Upload one or more branding assets (multipart). Pass only the fields you are
 * replacing; each key maps to the backend's expected multipart field name.
 */
export async function uploadBrandingAssets(
  id: string,
  files: Partial<Record<BrandingAssetKind, File>>
): Promise<ReportBranding> {
  const form = new FormData();
  (Object.keys(files) as BrandingAssetKind[]).forEach((k) => {
    const f = files[k];
    if (f) form.append(k, f);
  });
  const res = await requestMultipart<{ data: ReportBranding }>(
    `report-branding/${encodeURIComponent(id)}/assets`,
    form
  );
  return res.data;
}

// ---------- compiled branding (report preview) ----------

/** The compiled branding for one brand (brand override, else agency level). */
export async function resolveReportBranding(
  shopSlug: string
): Promise<ResolvedReportBranding> {
  const res = await request<{ data?: ResolvedReportBranding }>(
    `report-branding/resolve${toQuery({ shop_slug: shopSlug })}`,
    { method: 'GET' }
  );
  return res.data ?? {};
}

/** The compiled agency-level branding, the level the client portal renders with. */
export async function resolveOrganisationReportBranding(): Promise<ResolvedReportBranding> {
  const res = await request<{ data?: ResolvedReportBranding }>(
    `report-branding/resolve${toQuery({ scope: 'organisation' })}`,
    { method: 'GET' }
  );
  return res.data ?? {};
}

/**
 * The agency's report colour tokens for the workbench preview. Resolved at
 * organisation level, the same level the client portal uses, so the preview
 * matches what the client sees.
 *
 * Never throws: the preview must still render when branding can't be loaded
 * (network error, no branding configured), it just renders without the
 * agency defaults. The values are NOT validated here — the srcdoc builder
 * runs every entry through report-vars before injecting it.
 */
export async function getReportPreviewVars(): Promise<Record<string, string>> {
  try {
    const branding = await resolveOrganisationReportBranding();
    const vars = branding.css_vars;
    if (!vars || typeof vars !== 'object' || Array.isArray(vars)) return {};
    return vars as Record<string, string>;
  } catch {
    return {};
  }
}

// ---------- per-brand client-portal settings ----------

export async function getClientPortalSettings(
  slug: string
): Promise<ClientPortalSettings> {
  const res = await request<{ data: ClientPortalSettings }>(
    `shops/${encodeURIComponent(slug)}/client-portal-settings${toQuery({})}`,
    { method: 'GET' }
  );
  return res.data;
}

export async function updateClientPortalSettings(
  slug: string,
  body: Partial<ClientPortalSettings>
): Promise<ClientPortalSettings> {
  const res = await request<{ data: ClientPortalSettings }>(
    `shops/${encodeURIComponent(slug)}/client-portal-settings`,
    { method: 'PUT', body: JSON.stringify(body) }
  );
  return res.data;
}
